import { Component, computed, inject, InjectionToken, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { GAME_ID } from '../global/game-id';
import { FormConfig } from '../admin/config/base-form.config';
import { AppEvent, CoOrganiserDetail } from './models';
import { EventService } from './event.service';
import { UserService } from '../global/userService/user-service';
import { Modal } from '../global/modal';
import { buildEventCreateForm } from './event-form.config';

export const EVENT_FORM_CONFIG = new InjectionToken<Observable<FormConfig>>('EVENT_FORM_CONFIG');

/**
 * The creation step's form, kept separate from EVENT_FORM_CONFIG because the
 * edit page injects that one and must keep the full form.
 *
 * Carries its own factory default: the creation step asks for a title and a
 * date, neither of which varies by game, so there is nothing for a game module
 * to provide. A game that later needs a different creation step can still
 * override the token in its own module.
 */
export const EVENT_CREATE_FORM_CONFIG = new InjectionToken<Observable<FormConfig>>('EVENT_CREATE_FORM_CONFIG', {
    factory: () => buildEventCreateForm()
});

@Component({
    standalone: false,
    selector: 'abs-event-create',
    template: `
        <abs-page-layout [pageTitle]="'GLOBAL.EVENTS.CREATE_TITLE' | translate">
            @if (organiserDisplayName()) {
                <ion-item lines="none">
                    <ion-label>
                        <h3>{{ 'GLOBAL.EVENTS.DETAIL.ORGANISER' | translate }}</h3>
                        <p>{{ organiserDisplayName() }}</p>
                    </ion-label>
                </ion-item>
            } @else {
                <ion-item lines="none" color="warning">
                    <ion-label class="ion-text-wrap">
                        <p>{{ 'GLOBAL.EVENTS.SET_DISPLAY_NAME_PROMPT' | translate }}</p>
                    </ion-label>
                    <ion-button slot="end" size="small" (click)="promptSetDisplayName()">
                        {{ 'GLOBAL.EVENTS.SET_DISPLAY_NAME_BUTTON' | translate }}
                    </ion-button>
                </ion-item>
            }

            <p class="content">{{ 'GLOBAL.EVENTS.CREATE_LEAD_IN' | translate }}</p>

            @if (formConfig$ | async; as formConfig) {
                <abs-form [config]="formConfig" (formSubmit)="onSubmit($event.formValue)">
                    <abs-co-organiser-picker
                        [coOrganiserDetails]="coOrganiserDetails"
                        (coOrganisersChange)="coOrganiserIds = $event"
                    ></abs-co-organiser-picker>

                    @if (saveError) {
                        <ion-item color="danger" lines="none">
                            <ion-label class="ion-text-wrap">{{ saveError }}</ion-label>
                        </ion-item>
                    }

                    <ion-button type="submit" [disabled]="!organiserDisplayName()">
                        {{ 'GLOBAL.EVENTS.SAVE_DRAFT' | translate }}
                    </ion-button>
                </abs-form>
            }

            <ng-template #displayNameForm>
                <abs-registration-details-form
                    [name]="displayNameValue"
                    [showContactEmail]="false"
                    (nameChange)="displayNameValue = $event"
                    (validChange)="displayNameValid = $event"
                ></abs-registration-details-form>
            </ng-template>
            <ng-template #displayNameButtons>
                <ion-button [disabled]="!displayNameValid" (click)="saveDisplayName()">
                    {{ 'GLOBAL.SAVE' | translate }}
                </ion-button>
            </ng-template>
        </abs-page-layout>
    `
})
export class EventCreatePage implements OnInit {
    private eventService = inject(EventService);
    private userService = inject(UserService);
    private translate = inject(TranslateService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private modal = inject(Modal);
    gameId = inject(GAME_ID);
    formConfig$ = inject(EVENT_CREATE_FORM_CONFIG);

    organiserDisplayName = computed(() => this.userService.user()?.displayName);

    coOrganiserDetails: CoOrganiserDetail[] = [];
    coOrganiserIds: string[] = [];
    saveError: string | null = null;

    @ViewChild('displayNameForm') displayNameForm: TemplateRef<any>;
    @ViewChild('displayNameButtons') displayNameButtons: TemplateRef<any>;

    displayNameValue = '';
    displayNameValid = false;

    ngOnInit() {}

    onSubmit(formValue: Partial<AppEvent>) {
        this.saveError = null;
        const event: Partial<AppEvent> = {
            ...formValue,
            gameId: this.gameId,
            publishStatus: 'draft',
            coOrganisers: this.coOrganiserIds
        };
        this.eventService.createEvent(event).subscribe({
            next: (created) => {
                this.router.navigate(['..', created._id], { relativeTo: this.route });
            },
            error: (err) => {
                this.saveError = err?.error?.message || 'An error occurred while saving. Please check all fields and try again.';
            }
        });
    }

    promptSetDisplayName() {
        this.displayNameValue = '';
        this.displayNameValid = false;
        this.modal.show({
            props: {
                title: this.translate.instant('GLOBAL.EVENTS.SET_DISPLAY_NAME_HEADER'),
                contentTemplate: this.displayNameForm,
                buttonsTemplate: this.displayNameButtons
            }
        });
    }

    saveDisplayName() {
        const name = this.displayNameValue.trim();
        if (!name) {
            return;
        }
        this.userService.updateDisplayName(name).subscribe(() => {
            this.userService.refreshAccessToken();
        });
    }
}
